interface SearchResult {
  startIndex: number
  endIndex: number
}

export function findTextRanges(root: Node, searchTexts: string | string[]): Range[] {
  const searchTerms: string[] = Array.isArray(searchTexts) ? searchTexts : [searchTexts]
  const searchTermsSet = new Set(searchTerms)

  // 1. 모든 검색어의 위치 찾기
  const text = root.textContent?.toLocaleLowerCase() ?? ''
  const results: SearchResult[] = []

  searchTermsSet.forEach((searchText) => {
    if (!searchText) return

    let startIndex = 0
    while (true) {
      const index = text.indexOf(searchText.toLocaleLowerCase(), startIndex)
      if (index === -1) break

      results.push({
        startIndex: index,
        endIndex: index + searchText.length,
      })
      startIndex = index + searchText.length
    }
  })

  // 2. 시작 위치 기준으로 정렬
  results.sort((a, b) => a.startIndex - b.startIndex)

  // 3. 각 결과에 대한 DOM Range 생성
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)

  let currentNode: Node | null = null
  let currentIndex = 0

  return results
    .map((result) => {
      const range = document.createRange()

      while (currentIndex < result.endIndex) {
        currentNode ??= walker.nextNode()
        if (!currentNode) return null

        const nodeEndIndex = currentIndex + currentNode.textContent.length

        if (currentIndex <= result.startIndex && result.startIndex < nodeEndIndex) {
          range.setStart(currentNode, result.startIndex - currentIndex)
        }

        if (currentIndex <= result.endIndex && result.endIndex <= nodeEndIndex) {
          range.setEnd(currentNode, result.endIndex - currentIndex)
          return range
        }

        currentIndex = nodeEndIndex
        currentNode = null
      }
    })
    .filter(Boolean)
    .filter((range) => !spansMultipleLines(range))
}

const spansMultipleLines = (range: Range): boolean => {
  const commonAncestor = range.commonAncestorContainer
  const { startContainer, endContainer } = range

  // 줄바꿈을 발생시키는 display 값들
  const lineBreakDisplays = new Set([
    'block',
    'flex',
    'grid',
    'table',
    'flow-root',
    'list-item',
    'table-row',
    'table-cell',
    'table-caption',
  ])

  // 노드의 computed style을 확인하여 줄바꿈을 일으키는지 확인
  const causesLineBreak = (node: Node): boolean => {
    if (node.nodeType !== Node.ELEMENT_NODE) return false

    const element = node as Element
    const computedStyle = window.getComputedStyle(element)

    // display 속성 체크
    if (lineBreakDisplays.has(computedStyle.display)) return true

    // float 속성 체크 (left/right는 레이아웃 변경 가능)
    if (computedStyle.float !== 'none') return true

    // position 속성 체크 (absolute/fixed는 일반 흐름에서 제외)
    if (computedStyle.position === 'absolute' || computedStyle.position === 'fixed') return true

    return false
  }

  // 공통 조상까지 모든 부모 노드 가져오기
  const getParentsUpToAncestor = (node: Node, ancestor: Node): Node[] => {
    const parents: Node[] = []
    let current: Node | null = node

    while (current && current !== ancestor) {
      parents.push(current)
      current = current.parentNode
    }

    return parents
  }

  // start와 end 컨테이너 사이의 모든 요소 찾기
  const startParents = getParentsUpToAncestor(startContainer, commonAncestor)
  const endParents = getParentsUpToAncestor(endContainer, commonAncestor)

  // 시작과 끝의 경로에 줄바꿈 요소가 있는지 확인
  const hasLineBreakInStartPath = startParents.some(causesLineBreak)
  const hasLineBreakInEndPath = endParents.some(causesLineBreak)

  return hasLineBreakInStartPath || hasLineBreakInEndPath
}
